export type LineDiffEntry =
    | { type: "remove"; line: number; text: string }
    | { type: "add"; line: number; text: string };

/**
 * Line-oriented Myers diff. Returns only changed lines (removes/adds in order).
 * Line numbers are 1-based in their respective texts (before for remove, after for add).
 *
 * Prefer Myers over LCS backtracking: duplicate YAML lines no longer explode a
 * single mid-file insert into “every line below changed.”
 */
export function computeLineDiff(before: string, after: string): LineDiffEntry[] {
    if (before === after) return [];
    return myersLineDiff(splitLines(before), splitLines(after));
}

/** Empty string → no lines (unlike String#split, which yields [""]). */
function splitLines(text: string): string[] {
    if (text.length === 0) return [];
    return text.split("\n");
}

function myersLineDiff(a: readonly string[], b: readonly string[]): LineDiffEntry[] {
    const n = a.length;
    const m = b.length;
    if (n === 0) {
        return b.map((text, idx) => ({ type: "add" as const, line: idx + 1, text }));
    }
    if (m === 0) {
        return a.map((text, idx) => ({ type: "remove" as const, line: idx + 1, text }));
    }

    const max = n + m;
    const offset = max;
    const v = new Int32Array(2 * max + 1);
    v.fill(-1);
    v[offset + 1] = 0;

    /** Per-depth snapshot of V indexed as k + offset → x */
    const trace: Int32Array[] = [];

    let doneD = -1;
    for (let d = 0; d <= max; d += 1) {
        trace.push(Int32Array.from(v));
        for (let k = -d; k <= d; k += 2) {
            const kIdx = k + offset;
            let x: number;
            if (k === -d || (k !== d && v[kIdx - 1] < v[kIdx + 1])) {
                x = v[kIdx + 1]; // vertical → insert from b
            } else {
                x = v[kIdx - 1] + 1; // horizontal → delete from a
            }
            let y = x - k;
            while (x < n && y < m && a[x] === b[y]) {
                x += 1;
                y += 1;
            }
            v[kIdx] = x;
            if (x >= n && y >= m) {
                doneD = d;
                break;
            }
        }
        if (doneD >= 0) break;
    }

    if (doneD < 0) {
        return [
            ...a.map((text, idx) => ({ type: "remove" as const, line: idx + 1, text })),
            ...b.map((text, idx) => ({ type: "add" as const, line: idx + 1, text })),
        ];
    }

    type Seg =
        | { kind: "remove"; a0: number; a1: number }
        | { kind: "add"; b0: number; b1: number };
    const segs: Seg[] = [];

    let x = n;
    let y = m;
    for (let d = doneD; d > 0; d -= 1) {
        const vPrev = trace[d];
        const k = x - y;
        const kIdx = k + offset;

        const prevK =
            k === -d || (k !== d && vPrev[kIdx - 1] < vPrev[kIdx + 1]) ? k + 1 : k - 1;

        const prevX = vPrev[prevK + offset];
        const prevY = prevX - prevK;

        // Walk back along the snake (equals) to the end of the edit move.
        while (x > prevX && y > prevY) {
            x -= 1;
            y -= 1;
        }

        if (x === prevX) {
            segs.push({ kind: "add", b0: prevY, b1: prevY + 1 });
        } else {
            segs.push({ kind: "remove", a0: prevX, a1: prevX + 1 });
        }
        x = prevX;
        y = prevY;
    }

    segs.reverse();

    const out: LineDiffEntry[] = [];
    for (const seg of segs) {
        if (seg.kind === "remove") {
            for (let i = seg.a0; i < seg.a1; i += 1) {
                out.push({ type: "remove", line: i + 1, text: a[i] });
            }
        } else {
            for (let j = seg.b0; j < seg.b1; j += 1) {
                out.push({ type: "add", line: j + 1, text: b[j] });
            }
        }
    }
    return out;
}
